import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Msg, panelGet } from '../../kit';
import { Check, EmailPreview, fmtFrom, inputCls } from '../emailKit';
import { Pill, SECTION, Trigger, inBody, num } from './shared';
import { reasonLabel } from './DryRun';
import { NextSendPanel, Schedules, normSchedule } from './Schedule';

/**
 * Preview by cohort (backend: memac.cohort_preview, GET
 * email/memac_cohort_preview; owner, 2026-10-25: "preview all emails for each
 * cohort what will be sent, as of when we open it").
 *
 * For a trigger, every segment it applies to (status × cohort): the mail one
 * member of it would get if it went out now — subject, preheader, the HTML
 * with every placeholder filled from values recomputed when the preview
 * opens — who that member is (switch to another, or type an address), how many
 * in the segment would get it, and why the rest would not. "Preview all"
 * pages through every chosen trigger × segment.
 *
 * Who is shown (owner, 2026-10-27: "only the ones to be sent/already sent for
 * the cohort should be shown"): by default only the segments where someone
 * would get it now or already received it, and inside one only those people
 * (would get it now first, then already sent). "Already sent" is this
 * occurrence only (a monthly mail's earlier months, or a round's earlier
 * rounds, do not count); recipients who left the audience are counted apart,
 * so the empty state never says nobody received it while the ledger has
 * sends. "Show everyone in the audience" brings back every member, with the
 * reason the rest would not get it. "Preview for a specific person" works for anyone either way.
 *
 * Reads only: nothing is sent, nothing is recorded (no ledger row, no run
 * row), and the links in a preview are samples — a click unsubscribes nobody.
 */

type Mail = {
  subject: string; preheader: { text: string; source: string }; html: string; text: string;
  from_email: string; from_name: string; reply_to: string; send_html: boolean;
  blank: string[]; fell_back: string[]; footer_added: boolean; rendered_for: string; error?: string;
};
type Member = {
  user_id: string; email: string; name: string; state: string; reason: string | null; reason_words: string | null;
  /** Every mail of this trigger they received (any occurrence). */
  sent_count?: number; last_sent_at?: string | null;
  /** This occurrence only ("already sent" means this one). */
  occ_sent_count?: number; occ_sent_at?: string | null;
};
type Rep = Member & { status?: string; cohort?: string; pick: number; of: number };
type Segment = {
  key: string; status: string; status_label: string; cohort: string; cohort_label: string;
  members: number; detected: number; eligible: number;
  /** Already received this occurrence and would not get it again now. */
  sent?: number;
  /** Had an earlier occurrence only (a monthly mail's earlier months): not listed by default. */
  sent_earlier?: number;
  /** Someone would get it now or already received it: listed by default. */
  in_focus?: boolean;
  /** How many the member pager walks in this view. */
  shown?: number;
  excluded: Record<string, number>; capped: Record<string, number>;
  would_send: boolean; why: string | null; representative: Rep; members_sample: Member[]; mail: Mail | null;
};
type Person = Rep & { segment: string | null; in_audience: boolean; mail: Mail | null };
export type Preview = {
  trigger: { id: string; name: string; category_label: string; signal_label: string; template_key: string | null;
    template_name: string | null; content_mode: string; enabled: boolean; mode: string; is_service: boolean };
  generated_at: string; as_of: string; values_now: Record<string, string>;
  view?: 'focused' | 'everyone';
  /** What "already received" refers to: words = "it" | "this month's mail" | … */
  occurrence?: { key: string | null; per_person: boolean; words: string };
  totals: { members: number; detected: number; eligible: number; sent?: number; segments_in_focus?: number;
    /** Had an earlier occurrence only. */
    sent_earlier?: number;
    /** Received this occurrence but outside every segment now (left the audience, or our team). */
    sent_outside?: number; received_ever?: number;
    excluded: Record<string, number>; capped: Record<string, number> };
  notes: string[]; warnings: string[]; content_error: string | null; schedule: any;
  segments: Segment[]; person: Person | null;
};

const fetchPreview = (params: Record<string, string | number>) =>
  panelGet(SECTION, 'memac_cohort_preview', params) as Promise<{ data: Preview | null; error: string }>;
const EVERYONE_LABEL = 'Show everyone in the audience';

const STATE_WORDS: Record<string, string> = {
  eligible: 'would get it now', sent: 'already sent', left_out: 'left out', not_due: 'not due right now',
  sample: 'sample values',
};
const shortDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const sentWords = (m: Member | Rep) => {
  const n = Number(m.sent_count || 0);
  const on = shortDate(m.last_sent_at);
  return `${on ? `on ${on}` : ''}${n > 1 ? `${on ? ', ' : ''}${num(n)} times` : ''}`;
};
/** "on 10 Oct 2026" for this occurrence, "(3 mails in all)" when they had earlier ones too. */
const occWords = (m: Member | Rep) => {
  const on = shortDate(m.occ_sent_at ?? m.last_sent_at);
  const all = Number(m.sent_count || 0);
  const here = Number(m.occ_sent_count ?? all);
  return `${on ? `on ${on}` : ''}${all > here ? ` (${num(all)} mails in all)` : ''}`;
};
const why = (m: Member | Rep) => m.reason_words || (m.reason ? reasonLabel(m.reason) : '');
const memberState = (m: Member | Rep) => {
  if (m.state === 'left_out') return `left out — ${why(m)}${Number(m.sent_count || 0) > 0 ? ` (had an earlier one ${sentWords(m)})` : ''}`;
  if (m.state === 'sent') {
    const when = occWords(m);
    // "already sent for this occurrence" says the same thing again.
    const again = m.reason && m.reason !== 'occurrence' ? ` — not again now: ${why(m)}` : '';
    return `already sent${when ? ` ${when}` : ''}${again}`;
  }
  if (m.state === 'eligible' && Number(m.sent_count || 0) > 0) return `would get it now (also had it ${sentWords(m)})`;
  if (m.state === 'not_due' && Number(m.sent_count || 0) > 0) return `not due right now (had an earlier one ${sentWords(m)})`;
  return STATE_WORDS[m.state] || m.state;
};
const occText = (p: Preview | null | undefined) => p?.occurrence?.words || 'it';
/** "2 received it and are no longer in its audience; 3 had an earlier one" — the recipients the
 *  segments do not count (so "nobody has received it" is never claimed while the ledger has sends). */
const elsewhereText = (t: Preview['totals'] | undefined) => {
  const out: string[] = [];
  const o = Number(t?.sent_outside || 0);
  const e = Number(t?.sent_earlier || 0);
  if (o) out.push(`${num(o)} received it and ${o === 1 ? 'is' : 'are'} no longer in its audience`);
  if (e) out.push(`${num(e)} had an earlier one`);
  return out.join('; ');
};
const stateTone = (st: string): 'ok' | 'sky' | 'warn' | 'mute' =>
  st === 'eligible' ? 'ok' : st === 'sent' ? 'sky' : st === 'left_out' ? 'warn' : 'mute';
/** Listed in the default view: someone would get it now or already received it. */
const inFocus = (s: Segment) => (s.in_focus ?? (s.would_send || Number(s.sent || 0) > 0));
const reasonsText = (o: Record<string, number>) =>
  Object.entries(o).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${num(n)} ${reasonLabel(k).toLowerCase()}`).join(', ');
const segLabel = (s: Segment) => `${s.status_label} · ${s.cohort_label}`;

/** The big overlay both views use (above the trigger drawer). */
const Overlay: React.FC<{
  title: React.ReactNode; subtitle?: React.ReactNode; right?: React.ReactNode; onClose: () => void; children: React.ReactNode;
}> = ({ title, subtitle, right, onClose, children }) => {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return inBody(
    <div className="fixed inset-0 z-[60] bg-black/40 flex justify-center items-stretch p-2 md:p-5" onClick={onClose}>
      <div role="dialog" aria-modal="true" className="bg-grayscale-100 w-full max-w-[1400px] rounded-xl overflow-hidden flex flex-col shadow-xl"
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-grayscale-300 bg-white flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-grayscale-1300 font-grotesk truncate">{title}</h2>
            {subtitle && <div className="text-xs text-grayscale-1100">{subtitle}</div>}
          </div>
          <div className="flex items-center gap-2 shrink-0">{right}<Btn onClick={onClose}>Close</Btn></div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">{children}</div>
      </div>
    </div>,
  );
};

// ---------------------------------------------------------------------------
// One segment
// ---------------------------------------------------------------------------
const MailBlock: React.FC<{ mail: Mail; who: Rep | Person | null; isService: boolean }> = ({ mail, who, isService }) => {
  if (mail.error) return <Msg kind="error">This mail could not be rendered: {mail.error}</Msg>;
  const member = who && who.state !== 'sample' && who.email;
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <div className="rounded-lg border border-grayscale-300 bg-white px-3 py-2 flex flex-col gap-1">
        <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Subject</div>
        <div className="text-sm font-bold text-grayscale-1300 break-words">{mail.subject || '(no subject)'}</div>
        <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 mt-1">Preheader (inbox preview line)</div>
        <div className="text-xs text-grayscale-1200 break-words">{mail.preheader?.text || '—'}</div>
        <div className="text-[10px] text-grayscale-900">
          {mail.preheader?.source === 'hidden' ? 'From the mail\'s hidden preheader.'
            : 'No hidden preheader: inboxes show the opening words, as here.'}
        </div>
      </div>
      {(mail.blank.length > 0 || mail.fell_back.length > 0) && (
        <div className="text-[11px] text-amber-800">
          {mail.blank.length > 0 && <>Blank for {member ? who!.email : 'the sample'}: {mail.blank.map(x => `{${x}}`).join(', ')}. </>}
          {mail.fell_back.length > 0 && <>Took the fallback: {mail.fell_back.map(x => `{${x}}`).join(', ')}.</>}
        </div>
      )}
      <EmailPreview subject={mail.subject} html={mail.html} text={mail.text} from={fmtFrom(mail.from_name, mail.from_email)}
        replyTo={mail.reply_to} to={member ? who!.email : undefined} plainOnly={!mail.send_html} height={560}
        banner={(
          <div className="text-[11px] text-grayscale-1100">
            Filled in from {member ? `${who!.email}'s account` : 'sample values (nobody in this segment)'} with the values as of
            this preview.{mail.footer_added ? ' MEMAC adds the unsubscribe line at the bottom.' : ''}
            {isService ? ' A service mail: no unsubscribe line.' : ''} The links are samples: a click unsubscribes nobody and
            logs nothing. Nothing was sent or recorded.
          </div>
        )} />
    </div>
  );
};

const SegmentView: React.FC<{
  seg: Segment; p: Preview; busy: boolean; everyone: boolean; onPick: (i: number) => void;
}> = ({ seg, p, busy, everyone, onPick }) => {
  const rep = seg.representative;
  const nLeft = Object.values(seg.excluded).reduce((a, b) => a + b, 0);
  const nCap = Object.values(seg.capped).reduce((a, b) => a + b, 0);
  const nSent = Number(seg.sent || 0);
  const hasRep = rep.state !== 'sample' && rep.state !== 'none' && !!rep.user_id;
  return (
    <div className="flex flex-col gap-3 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-grayscale-1300 font-grotesk">{segLabel(seg)}</span>
        {seg.would_send
          ? <Pill tone="ok">would go now to {num(seg.eligible)} of {num(seg.members)}</Pill>
          : nSent > 0 ? <Pill tone="sky">would not go now · {num(nSent)} already sent</Pill>
            : <Pill tone={seg.members ? 'warn' : 'mute'}>{seg.members ? 'would not go now' : 'empty segment'}</Pill>}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-grayscale-1200">
        <span className={seg.eligible ? 'text-green-700' : ''}><b>{num(seg.eligible)}</b> would get it now</span>
        <span className={nSent ? 'text-sky-800' : ''}><b>{num(nSent)}</b> already sent</span>
        <span><b>{num(seg.members)}</b> in the segment</span>
        <span><b>{num(seg.detected)}</b> due now</span>
        {nLeft > 0 && <span>left out: {reasonsText(seg.excluded)}</span>}
        {nCap > 0 && <span>capped: {reasonsText(seg.capped)}</span>}
      </div>
      {seg.why && <div className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-1.5">{seg.why}</div>}
      {hasRep && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-grayscale-1100">Shown for</span>
          <b className="text-grayscale-1300">{rep.name || rep.email || rep.user_id}</b>
          {rep.email && <span className="text-grayscale-1100">&lt;{rep.email}&gt;</span>}
          <Pill tone={stateTone(rep.state)}>{memberState(rep)}</Pill>
          <span className="flex-1" />
          <Btn small disabled={busy || rep.of <= 1} onClick={() => onPick((rep.pick - 1 + rep.of) % rep.of)}>‹ Previous member</Btn>
          <span className="tabular-nums text-grayscale-1100"
            title={everyone ? 'Everyone in the segment' : 'Only who would get it now, then who already received it'}>
            {rep.pick + 1} / {num(rep.of)}{everyone ? ' in the segment' : ' · would get it or already sent'}
          </span>
          <Btn small disabled={busy || rep.of <= 1} onClick={() => onPick((rep.pick + 1) % rep.of)}>Next member ›</Btn>
          {seg.members_sample.length > 1 && (
            <select className={`${inputCls} !w-64 !h-8 text-xs`} value={rep.pick < seg.members_sample.length ? String(rep.pick) : ''}
              disabled={busy} onChange={e => { if (e.target.value !== '') onPick(Number(e.target.value)); }}>
              {rep.pick >= seg.members_sample.length && <option value="">member {rep.pick + 1}</option>}
              {seg.members_sample.map((m, i) => (
                <option key={m.user_id} value={i}>{(m.name || m.email || m.user_id)} — {memberState(m)}</option>
              ))}
            </select>
          )}
        </div>
      )}
      {busy && <div className="text-[11px] text-grayscale-1100">Rendering…</div>}
      {seg.mail ? <MailBlock mail={seg.mail} who={hasRep ? rep : null} isService={p.trigger.is_service} />
        : p.content_error ? <Msg kind="error">{p.content_error}</Msg>
          : <div className="text-xs text-grayscale-1100">
            {rep.state === 'none' ? `Nobody in this segment would get it now or has received ${occText(p)}.${Number(seg.sent_earlier || 0) ? ` ${num(seg.sent_earlier || 0)} had an earlier one.` : ''} Tick “${EVERYONE_LABEL}” to see its members and why.`
              : seg.members ? 'Not rendered yet.' : 'Nobody to show.'}</div>}
    </div>
  );
};

/** Who a typed address is, for this trigger, and their mail. */
const PersonView: React.FC<{ person: Person; p: Preview; onBack: () => void }> = ({ person, p, onBack }) => {
  const seg = p.segments.find(s => s.key === person.segment);
  return (
    <div className="flex flex-col gap-3 min-w-0">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-sm font-bold text-grayscale-1300 font-grotesk">{person.name || person.email}</span>
        <span className="text-grayscale-1100">&lt;{person.email}&gt;</span>
        <Pill tone={stateTone(person.state)}>{memberState(person)}</Pill>
        <span className="text-grayscale-1100">{seg ? `segment ${segLabel(seg)}` : person.in_audience ? '' : 'outside this trigger\'s audience'}</span>
        <span className="flex-1" />
        <Btn small onClick={onBack}>Back to the segments</Btn>
      </div>
      {person.mail ? <MailBlock mail={person.mail} who={person} isService={p.trigger.is_service} />
        : <div className="text-xs text-grayscale-1100">No mail to show.</div>}
    </div>
  );
};

const segDot = (s: Segment) => (s.would_send ? 'bg-green-600' : Number(s.sent || 0) > 0 ? 'bg-sky-600'
  : s.members ? 'bg-amber-500' : 'bg-grayscale-400');

const SegmentNav: React.FC<{
  segs: Segment[]; active: string; everyone: boolean; onPick: (k: string) => void;
}> = ({ segs, active, everyone, onPick }) => {
  const byStatus: { status: string; label: string; items: Segment[] }[] = [];
  segs.forEach(s => {
    let g = byStatus.find(x => x.status === s.status);
    if (!g) { g = { status: s.status, label: s.status_label, items: [] }; byStatus.push(g); }
    g.items.push(s);
  });
  return (
    <div className="flex flex-col gap-2">
      {byStatus.map(g => (
        <div key={g.status} className="flex flex-col gap-0.5">
          <div className="text-[10px] uppercase tracking-wide text-grayscale-1100 px-1">{g.label}</div>
          {g.items.map(s => (
            <button key={s.key} type="button" onClick={() => onPick(s.key)}
              className={`text-left rounded-md border px-2 py-1 text-xs flex items-center gap-2 ${active === s.key
                ? 'border-primary-500 bg-primary-50' : 'border-grayscale-300 bg-white hover:bg-grayscale-100'}`}>
              <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${segDot(s)}`} />
              <span className="flex-1 min-w-0 truncate">{s.cohort_label}</span>
              <span className="tabular-nums text-grayscale-1100 whitespace-nowrap"
                title={`${num(s.eligible)} would get it now · ${num(s.sent || 0)} already sent · ${num(s.members)} in the segment`}>
                {s.members ? `${num(s.eligible)} / ${num(s.sent || 0)} / ${num(s.members)}` : '—'}
              </span>
            </button>
          ))}
        </div>
      ))}
      <div className="text-[10px] text-grayscale-900 px-1">
        <span className="inline-block w-2 h-2 rounded-full bg-green-600 mr-1" />would go now ·
        <span className="inline-block w-2 h-2 rounded-full bg-sky-600 mx-1" />already sent only
        {everyone && <> · <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mx-1" />nobody would get it ·
          <span className="inline-block w-2 h-2 rounded-full bg-grayscale-400 mx-1" />empty</>}.
        {' '}Numbers: would get it now / already sent / in segment.
      </div>
    </div>
  );
};

const Header: React.FC<{ p: Preview; sch: Schedules; onElapsed?: () => void }> = ({ p, sch, onElapsed }) => {
  const s = p.schedule ? normSchedule(p.schedule) : undefined;
  const t = p.totals;
  const nLeft = Object.values(t.excluded || {}).reduce((a, b) => a + b, 0);
  const nCap = Object.values(t.capped || {}).reduce((a, b) => a + b, 0);
  const vals = Object.entries(p.values_now || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined);
  return (
    <div className="bg-white rounded-xl border border-grayscale-300 p-3 flex flex-col gap-2">
      <NextSendPanel s={s} sch={sch} onElapsed={onElapsed} compact />
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-grayscale-1200">
        <span>If it went out now: <b>{num(t.eligible)}</b> would get it</span>
        <span title={`Already received ${occText(p)}`}><b>{num(t.sent || 0)}</b> already sent</span>
        {Number(t.sent_earlier || 0) > 0 && <span><b>{num(t.sent_earlier || 0)}</b> had an earlier one</span>}
        {Number(t.sent_outside || 0) > 0 && <span><b>{num(t.sent_outside || 0)}</b> received it, no longer in the audience</span>}
        <span><b>{num(t.detected)}</b> due</span>
        {nLeft > 0 && <span>left out: {reasonsText(t.excluded)}</span>}
        {nCap > 0 && <span>capped: {reasonsText(t.capped)}</span>}
        <span className="text-grayscale-1100">{p.trigger.signal_label} · {p.trigger.category_label}
          {p.trigger.template_name ? ` · ${p.trigger.template_name}` : ''}</span>
      </div>
      {vals.length > 0 && (
        <div className="text-[11px] text-grayscale-1100 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>Live values (the same for everyone):</span>
          {vals.map(([k, v]) => <span key={k}><code>{`{${k}}`}</code> = <b className="text-grayscale-1300">{v}</b></span>)}
        </div>
      )}
      {[...p.notes, ...p.warnings].map((n, i) => <div key={i} className="text-[11px] text-amber-800">{n}</div>)}
    </div>
  );
};

// ---------------------------------------------------------------------------
// One trigger, every segment
// ---------------------------------------------------------------------------
/** everyone = the full audience view (every member, with the reason); the
 *  default pages only through who would get it now and who already did. */
export const usePreview = (triggerId: string | null, everyone = false) => {
  const [p, setP] = useState<Preview | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busySeg, setBusySeg] = useState('');
  const seq = useRef(0);
  const pickSeq = useRef(0);
  const ev = everyone ? '1' : '0';
  const load = useCallback(async (fresh = true) => {
    if (!triggerId) return;
    const mine = ++seq.current;
    setLoading(true); setError('');
    const { data, error: e } = await fetchPreview({ trigger_id: triggerId, render: 'all', fresh: fresh ? '1' : '0', everyone: ev });
    if (mine !== seq.current) return;
    setLoading(false);
    if (e || !data) { setError(e || 'No answer'); return; }
    setP(data);
  }, [triggerId, ev]);
  /** Another member of one segment (re-rendered on the server, the rest kept). */
  const pick = useCallback(async (segKey: string, i: number) => {
    if (!triggerId) return;
    const mine = ++pickSeq.current;
    const loadAt = seq.current;     // the load (and view) this pick belongs to
    setBusySeg(segKey);
    const { data, error: e } = await fetchPreview({ trigger_id: triggerId, segment: segKey, pick: i, fresh: '0', everyone: ev });
    if (mine !== pickSeq.current) return;          // a newer pick is on its way
    setBusySeg('');
    if (loadAt !== seq.current) return;            // reloaded meanwhile (e.g. "Show everyone" toggled): stale
    if (e || !data) { setError(e || 'No answer'); return; }
    const fresh = data.segments.find(s => s.key === segKey);
    // Only into the view it was asked for: a segment of the default view never lands in the full one.
    if (fresh) {
      setP(cur => (cur && (cur.view || 'focused') === (data.view || 'focused')
        ? { ...cur, segments: cur.segments.map(s => (s.key === segKey ? fresh : s)) } : cur));
    }
  }, [triggerId, ev]);
  const lookUp = useCallback(async (email: string) => {
    if (!triggerId) return '';
    setBusySeg('person');
    const { data, error: e } = await fetchPreview({ trigger_id: triggerId, user_email: email, render: 'none', fresh: '0', everyone: ev });
    setBusySeg('');
    if (e || !data) return e || 'No answer';
    setP(cur => (cur ? { ...cur, person: data.person } : data));
    return '';
  }, [triggerId, ev]);
  const clearPerson = useCallback(() => setP(cur => (cur ? { ...cur, person: null } : cur)), []);
  return { p, setP, error, loading, busySeg, load, pick, lookUp, clearPerson };
};

/** The segments of one preview, a nav on the left and the selected one on the right. */
/** The segments a view lists: by default only those where someone would get
 *  it now or already received it; with everyone, every segment with members
 *  (and the empty ones when asked). */
const listed = (segs: Segment[], everyone: boolean, showEmpty: boolean) =>
  segs.filter(s => (everyone ? showEmpty || s.members > 0 : inFocus(s)));

const PreviewBody: React.FC<{
  pv: ReturnType<typeof usePreview>; sch: Schedules; everyone: boolean; showEmpty: boolean; active: string;
  setActive: (k: string) => void; onEveryone: () => void; onElapsed?: () => void;
}> = ({ pv, sch, everyone: wanted, showEmpty, active, setActive, onEveryone, onElapsed }) => {
  const { p } = pv;
  const [as, setAs] = useState('');
  const [asErr, setAsErr] = useState('');
  if (!p) return null;
  // The view the answer on screen was made for (the toggle may be a step ahead while it reloads).
  const everyone = p.view ? p.view === 'everyone' : wanted;
  const segs = listed(p.segments, everyone, showEmpty);
  const seg = segs.find(s => s.key === active) || segs[0] || null;
  const hidden = everyone ? 0 : p.segments.filter(s => s.members > 0 && !inFocus(s)).length;
  const go = async () => {
    const e = as.trim().toLowerCase();
    if (!e) return;
    setAsErr(await pv.lookUp(e));
  };
  return (
    <>
      <Header p={p} sch={sch} onElapsed={onElapsed} />
      <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          <SegmentNav segs={segs} active={seg?.key || ''} everyone={everyone} onPick={k => { pv.clearPerson(); setActive(k); }} />
          {hidden > 0 && (
            <div className="text-[11px] text-grayscale-1100 px-1">
              {num(hidden)} other segment{hidden === 1 ? '' : 's'} with members {hidden === 1 ? 'is' : 'are'} not listed: nobody
              in {hidden === 1 ? 'it' : 'them'} would get it now or has received {occText(p)}.{' '}
              <button type="button" className="underline underline-offset-2 text-primary-700" onClick={onEveryone}>{EVERYONE_LABEL}</button>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-grayscale-1100">Preview for a specific person (their e-mail)</label>
            <div className="flex gap-1">
              <input value={as} onChange={e => setAs(e.target.value)} placeholder="name@example.com" spellCheck={false}
                onKeyDown={e => { if (e.key === 'Enter') void go(); }} className={`${inputCls} !h-8 text-xs`} />
              <Btn small onClick={() => { void go(); }} disabled={pv.busySeg === 'person' || !as.trim()}>Show</Btn>
            </div>
            {asErr && <div className="text-[11px] text-red-700">{asErr}</div>}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-grayscale-300 p-3 min-w-0">
          {p.person ? <PersonView person={p.person} p={p} onBack={pv.clearPerson} />
            : seg ? <SegmentView seg={seg} p={p} busy={pv.busySeg === seg.key} everyone={everyone}
              onPick={i => { void pv.pick(seg.key, i); }} />
              : everyone ? <div className="text-xs text-grayscale-1100">No segment has members. Tick “show empty segments” to see them.</div>
                : (
                  <div className="text-xs text-grayscale-1100 flex flex-col gap-2 items-start">
                    <span>Nobody would get this mail now, and nobody in its audience has received {occText(p)} — so there is no one to show.
                      {elsewhereText(p.totals) ? ` (${elsewhereText(p.totals)}.)` : ''}
                      {' '}To see the mail as each segment would get it, and why nobody would, show everyone in the audience.</span>
                    <Btn small onClick={onEveryone}>{EVERYONE_LABEL}</Btn>
                  </div>
                )}
        </div>
      </div>
    </>
  );
};

export const CohortPreview: React.FC<{
  triggerId: string; triggers: Trigger[]; sch: Schedules; onClose: () => void; onElapsed?: () => void;
}> = ({ triggerId, triggers, sch, onClose, onElapsed }) => {
  const [everyone, setEveryone] = useState(false);
  const pv = usePreview(triggerId, everyone);
  const [showEmpty, setShowEmpty] = useState(false);
  const [active, setActive] = useState('');
  // Values as of the open; switching the view keeps them (no recompute).
  const opened = useRef('');
  useEffect(() => {
    const first = opened.current !== triggerId;
    opened.current = triggerId;
    void pv.load(first);
  }, [triggerId, everyone]); // eslint-disable-line react-hooks/exhaustive-deps
  const t = triggers.find(x => x.id === triggerId);
  return (
    <Overlay onClose={onClose}
      title={<>Preview by cohort · {triggerId}{t ? ` · ${t.name}` : ''}</>}
      subtitle={pv.p ? <>As of {pv.p.as_of} — every placeholder filled from values recomputed when this opened. Nothing is sent or recorded.</>
        : 'Working out every segment…'}
      right={<>
        <Check checked={everyone} onChange={setEveryone}>{EVERYONE_LABEL}</Check>
        {everyone && <Check checked={showEmpty} onChange={setShowEmpty}>show empty segments</Check>}
        <Btn small onClick={() => { void pv.load(true); }} disabled={pv.loading}>{pv.loading ? 'Refreshing…' : 'Refresh (as of now)'}</Btn>
      </>}>
      {pv.error && <Msg kind="error">Preview failed: {pv.error}</Msg>}
      {!pv.p && pv.loading && <div className="text-sm text-grayscale-1100">Rendering the mail for each segment…</div>}
      {pv.p && pv.loading && <div className="text-[11px] text-grayscale-1100">Updating…</div>}
      <PreviewBody pv={pv} sch={sch} everyone={everyone} showEmpty={showEmpty} active={active} setActive={setActive}
        onEveryone={() => setEveryone(true)} onElapsed={onElapsed} />
    </Overlay>
  );
};

// ---------------------------------------------------------------------------
// Preview all: every chosen trigger × segment, one page at a time
// ---------------------------------------------------------------------------
export const PreviewAll: React.FC<{
  triggers: Trigger[]; sch: Schedules; onClose: () => void; onOpenTrigger: (id: string) => void; onElapsed?: () => void;
}> = ({ triggers, sch, onClose, onOpenTrigger, onElapsed }) => {
  const candidates = useMemo(() => triggers.filter(t => !t.external && !t.read_only && !t.on_hold), [triggers]);
  const enabled = candidates.filter(t => t.enabled);
  const withMail = candidates.filter(t => t.can_send);
  const [chosen, setChosen] = useState<string[]>(() => (enabled.length ? enabled : withMail).map(t => t.id));
  const [choosing, setChoosing] = useState(true);
  const [everyone, setEveryone] = useState(false);
  const [skipEmpty, setSkipEmpty] = useState(true);
  // Keyed by trigger and view: the two views page through different members.
  const [cache, setCache] = useState<Record<string, { p: Preview | null; error: string }>>({});
  const [ti, setTi] = useState(0);
  const [si, setSi] = useState(0);
  const toLast = useRef(false);
  const [busy, setBusy] = useState('');
  const first = useRef(true);
  const order = candidates.filter(t => chosen.includes(t.id)).map(t => t.id);
  const tid = order[ti] || '';
  const ck = (id: string) => `${id}|${everyone ? 'everyone' : 'focused'}`;

  const fetchOne = useCallback(async (id: string) => {
    const k = ck(id);
    if (!id || cache[k]) return;
    const fresh = first.current; first.current = false;   // values as of when this was opened
    setCache(c => ({ ...c, [k]: { p: null, error: '' } }));
    const { data, error } = await fetchPreview({ trigger_id: id, render: 'all', fresh: fresh ? '1' : '0', everyone: everyone ? '1' : '0' });
    setCache(c => ({ ...c, [k]: { p: data, error: error || (data ? '' : 'No answer') } }));
  }, [cache, everyone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (choosing || !tid) return;
    void fetchOne(tid);
    if (order[ti + 1]) void fetchOne(order[ti + 1]);       // the next one, ahead
  }, [choosing, tid, everyone]); // eslint-disable-line react-hooks/exhaustive-deps

  const entry = cache[ck(tid)];
  const cur = entry?.p || null;
  const segs = listed(cur?.segments || [], everyone, !skipEmpty);
  useEffect(() => {
    if (toLast.current && cur) { toLast.current = false; setSi(Math.max(0, segs.length - 1)); }
  }, [cur]); // eslint-disable-line react-hooks/exhaustive-deps
  const seg = segs[Math.min(si, Math.max(0, segs.length - 1))] || null;

  const next = useCallback(() => {
    if (si + 1 < segs.length) setSi(si + 1);
    else if (ti + 1 < order.length) { setTi(ti + 1); setSi(0); }
  }, [si, segs.length, ti, order.length]);
  const prev = useCallback(() => {
    if (si > 0) setSi(si - 1);
    else if (ti > 0) { toLast.current = true; setTi(ti - 1); setSi(0); }
  }, [si, ti]);
  useEffect(() => {
    if (choosing) return undefined;
    const k = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [choosing, next, prev]);

  const pickMember = async (segKey: string, i: number) => {
    const k = ck(tid);
    setBusy(segKey);
    const { data, error } = await fetchPreview({ trigger_id: tid, segment: segKey, pick: i, fresh: '0', everyone: everyone ? '1' : '0' });
    setBusy('');
    const fresh = data?.segments.find(s => s.key === segKey);
    if (error || !fresh) return;
    setCache(c => ({ ...c, [k]: { p: c[k]?.p ? { ...c[k].p!, segments: c[k].p!.segments.map(s => (s.key === segKey ? fresh : s)) } : data, error: '' } }));
  };

  const toggle = (id: string) => setChosen(c => (c.includes(id) ? c.filter(x => x !== id) : [...c, id]));
  const t = candidates.find(x => x.id === tid);

  return (
    <Overlay onClose={onClose}
      title="Preview all — every chosen trigger × segment"
      subtitle={choosing ? 'Pick the triggers to page through. Nothing is sent or recorded.'
        : cur ? `As of ${cur.as_of} (values recomputed when this opened). ← / → page through. Nothing is sent or recorded.` : 'Loading…'}
      right={!choosing && <>
        <Check checked={everyone} onChange={v => { setEveryone(v); setSi(0); }}>{EVERYONE_LABEL}</Check>
        {everyone && <Check checked={skipEmpty} onChange={v => { setSkipEmpty(v); setSi(0); }}>skip empty segments</Check>}
        <Btn small onClick={() => setChoosing(true)}>Change triggers</Btn>
      </>}>
      {choosing ? (
        <div className="bg-white rounded-xl border border-grayscale-300 p-4 flex flex-col gap-3">
          <div className="flex gap-2 flex-wrap items-center text-xs">
            <Btn small onClick={() => setChosen(enabled.map(x => x.id))}>Switched on ({enabled.length})</Btn>
            <Btn small onClick={() => setChosen(withMail.map(x => x.id))}>All with an e-mail ({withMail.length})</Btn>
            <Btn small onClick={() => setChosen([])}>None</Btn>
            <span className="text-grayscale-1100">
              {enabled.length ? '' : 'No trigger is switched on yet, so every trigger with an e-mail is picked. '}
              {order.length} chosen.
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1">
            {candidates.map(x => (
              <label key={x.id} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs cursor-pointer ${chosen.includes(x.id)
                ? 'border-primary-300 bg-primary-50' : 'border-grayscale-300 bg-white'}`}>
                <input type="checkbox" checked={chosen.includes(x.id)} onChange={() => toggle(x.id)} className="mt-0.5 accent-primary-500" />
                <span className="min-w-0">
                  <span className="font-mono">{x.id}</span> {x.name}
                  <span className="block text-[10px] text-grayscale-1100">
                    {x.enabled ? (x.mode === 'auto' ? 'On · auto' : 'On · manual') : 'Off'}{x.can_send ? '' : ' · no e-mail yet'}
                    {x.manual_only ? ' · by hand only' : ''}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <div className="flex justify-end">
            <Btn kind="primary" disabled={!order.length} onClick={() => { setChoosing(false); setTi(0); setSi(0); }}>
              Preview {order.length} trigger{order.length === 1 ? '' : 's'}
            </Btn>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-grayscale-300 px-3 py-2 flex items-center gap-2 flex-wrap">
            <Btn small onClick={prev} disabled={ti === 0 && si === 0}>‹ Previous</Btn>
            <div className="text-xs text-grayscale-1200 flex-1 min-w-0">
              Trigger <b>{ti + 1}</b> of {order.length} · <button type="button" className="font-mono text-primary-700 hover:underline"
                onClick={() => onOpenTrigger(tid)}>{tid}</button> {t?.name}
              {cur && <> — segment <b>{segs.length ? Math.min(si, segs.length - 1) + 1 : 0}</b> of {segs.length}
                {seg ? ` · ${segLabel(seg)}` : ''}</>}
            </div>
            <Btn small kind="primary" onClick={next} disabled={ti >= order.length - 1 && si >= segs.length - 1}>Next ›</Btn>
          </div>
          {entry?.error && <Msg kind="error">{tid}: {entry.error}</Msg>}
          {!cur && !entry?.error && <div className="text-sm text-grayscale-1100">Rendering {tid} for every segment…</div>}
          {cur && <Header p={cur} sch={sch} onElapsed={onElapsed} />}
          {cur && (seg ? (
            <div className="bg-white rounded-xl border border-grayscale-300 p-3 min-w-0">
              <SegmentView seg={seg} p={cur} busy={busy === seg.key} everyone={everyone}
                onPick={i => { void pickMember(seg.key, i); }} />
            </div>
          ) : (
            <div className="text-xs text-grayscale-1100 bg-white rounded-xl border border-grayscale-300 p-3">
              {everyone ? `No segment of ${tid} has members` : `Nobody would get ${tid} now, and nobody in its audience has received ${occText(cur)}`}
              {ti < order.length - 1 ? ' — Next moves on.' : '.'}
              {!everyone && elsewhereText(cur.totals) ? ` (${elsewhereText(cur.totals)}.)` : ''}
              {everyone ? '' : ` Tick “${EVERYONE_LABEL}” to see every segment.`}
            </div>
          ))}
        </>
      )}
    </Overlay>
  );
};
